import { JSX } from "react";
import { useRepositoriesContext } from "../../../../contexts/RepositoriesContext";
import LoadingElement from "../../../components/loading-element/loading-element";
import { Design } from "../../../../core/models/entities/Design";
import { ErrorView } from "../../../components/error-view/ErrorView";
import {
  DesignDetails,
  DesignModalIntent,
} from "../components/design-details.component";
import {
  ComponentState,
  useComponentStateMachine,
} from "../../../shared/hooks/useComponentStateMachine";
import { useDesignActionHandler } from "../hooks/useDesignActionHandler";

import { useApiClient } from "../../../../data/clients/ApiClient";
import { useQueryClient } from "@tanstack/react-query";
import { withToasts } from "../../../components/Toast/Toast";

interface WrapperProps {
  design: Design;
  onClose: () => void;
  onShowRejectionNote: (design: Design) => void;
  closeDialog: () => void;
  intent: DesignModalIntent;
}

export function DesignDetailsWrapper({
  design,
  onClose,
  onShowRejectionNote,
  closeDialog,
  intent,
}: WrapperProps): JSX.Element {
  const { designRepository } = useRepositoriesContext();
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const {
    data: summary,
    isLoading: isSummaryLoading,
    isSuccess: isSummarySuccess,
    isError: isSummaryError,
  } = designRepository.useDesign(design.customer.id, design.id);
  const { state, dataTransition } = useComponentStateMachine();
  const { handleDeleteDesign, handleRejectAndDelete } =
    useDesignActionHandler();

  dataTransition(isSummaryLoading, isSummarySuccess, isSummaryError);

  async function onDelete(design: Design) {
    try {
      await handleDeleteDesign(design);
      closeDialog();
    } catch (error) {
      console.error("Failed to delete design:", error);
    }
  }

  function onRejectAndDelete(design: Design, note?: string) {
    handleRejectAndDelete(design, note);
  }

  async function handleAssignCustomer(customerId: number) {
    const transaction = async () => {
      await apiClient.assignCustomerToDesign(
        design.customer.id,
        design.id,
        customerId
      );
      await queryClient.invalidateQueries(["designs"]);
      await queryClient.invalidateQueries(["designs", design.id]);
    };

    await withToasts(
      transaction,
      "Assigning customer...",
      "Customer assigned successfully",
      "Failed to assign customer"
    );
  }

  return state === ComponentState.Succeed ? (
    <DesignDetails
      onShowRejectionNote={onShowRejectionNote}
      design={summary!}
      onClose={onClose}
      closeDialog={closeDialog}
      intent={intent}
      onDeleteDesign={onDelete}
      onRejectAndDelete={onRejectAndDelete}
      onAssignCustomer={handleAssignCustomer}
    />
  ) : state === ComponentState.Loading ? (
    <div className={"w-220 h-128 relative"}>
      <LoadingElement />
    </div>
  ) : (
    <div className={"w-220 h-128 relative"}>
      <ErrorView />
    </div>
  );
}
